import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Classification } from '@/types'

const PRESET_COLORS = [
  { name: 'Gray', hex: '#6B7280' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Green', hex: '#22C55E' },
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Teal', hex: '#14B8A6' },
]

interface EditClassificationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  classification: Classification | null
  onClassificationUpdated: () => void
}

export function EditClassificationDialog({
  open,
  onOpenChange,
  groupId,
  classification,
  onClassificationUpdated,
}: EditClassificationDialogProps) {
  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState(PRESET_COLORS[0].hex)
  const [alertEnabled, setAlertEnabled] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (classification && open) {
      setDisplayName(classification.displayName)
      setDescription(classification.description || '')
      setColor(classification.color)
      setAlertEnabled(classification.alertEnabled)
      setError(null)
    }
  }, [classification, open])

  const handleSave = async () => {
    if (!classification) return

    if (!displayName.trim()) {
      setError(t('editClassification.displayNameRequired'))
      return
    }

    setSaving(true)
    setError(null)

    try {
      const updateClassificationFn = apiCallable('updateClassification')
      await updateClassificationFn({
        groupId,
        classificationId: classification.id,
        displayName: displayName.trim(),
        description: description.trim() || null,
        alertEnabled,
        color,
      })

      onOpenChange(false)
      onClassificationUpdated()
    } catch (err: any) {
      console.error('Failed to update classification:', err)
      if (err.code === 'permission-denied') {
        setError(t('editClassification.noPermission'))
      } else {
        setError(t('editClassification.failedToUpdate'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setError(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('editClassification.title')}</DialogTitle>
          <DialogDescription>
            {t('editClassification.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="editDisplayName">{t('editClassification.displayName')}</Label>
            <Input
              id="editDisplayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('editClassification.displayNamePlaceholder')}
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="editDescription">{t('editClassification.descriptionLabel')}</Label>
            <Input
              id="editDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('editClassification.descriptionPlaceholder')}
            />
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>{t('editClassification.color')}</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset.hex}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === preset.hex
                      ? 'border-foreground scale-110'
                      : 'border-transparent hover:border-muted-foreground/50'
                  }`}
                  style={{ backgroundColor: preset.hex }}
                  onClick={() => setColor(preset.hex)}
                  title={preset.name}
                />
              ))}
            </div>
          </div>

          {/* Alert Enabled */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-normal">{t('editClassification.enableAlerts')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('editClassification.alertsHint')}
              </p>
            </div>
            <Switch
              checked={alertEnabled}
              onCheckedChange={setAlertEnabled}
            />
          </div>

          {/* Preview */}
          {displayName.trim() && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: color }}
              />
              <span className="font-medium text-sm">{displayName.trim()}</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {alertEnabled ? t('classifications.alertsOn') : t('classifications.alertsOff')}
              </span>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving} className="hidden sm:inline-flex">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || !displayName.trim()}>
            {saving ? t('common.saving') : t('common.saveChanges')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
